import type { AgentConfigChipProps } from '../AgentConfigChip';
import { AGENT_CHIP_TONE } from '../AgentConfigChip';
import copies from '../copies';

import type { ClaimActionExecutionMode, EffectiveClaimActionConfig } from './types';

const MODE_LABELS: Record<ClaimActionExecutionMode, string> = {
  always_human: 'Always human',
  always_ai: 'Always AI',
  // The selector's label, not the table's: on this mode the table shows the threshold itself,
  // which is the value that actually decides whether the agent may act.
  above_threshold: 'Confidence threshold',
};

/**
 * Human-readable "automatic execution" summary of an action for the read-only actions table.
 *
 * `above_threshold` renders the threshold rather than the mode name: the number is what the
 * mode means, and two actions on the same mode with different thresholds must not read alike.
 */
export const executionModeToAutomaticExecutionLabel = (action: EffectiveClaimActionConfig): string =>
  action.execution_mode === 'above_threshold'
    ? `Confidence: ${action.confidence_threshold}`
    : MODE_LABELS[action.execution_mode];

/** What the actions table hands `AgentConfigChip` for the Automatic Execution column. */
export type AutomaticExecutionChipProps = Pick<AgentConfigChipProps, 'label' | 'tone'>;

/**
 * The same summary as a tinted chip: its label, and the tone that label should carry.
 *
 * **A disabled action reports `Disabled`, whatever its mode.** The mode says what the agent *may*
 * do once the action is available to it at all, so rendering a threshold beside an action the
 * agent cannot call would describe a policy that is not in force. The Enabled column states the
 * same fact on its own; this keeps the two columns from contradicting each other.
 *
 * `always_ai` is the only affirmative tone — it is the one mode where the agent acts unattended
 * every time. `above_threshold` is `CONDITIONAL` because it sometimes does, and `always_human`
 * shares `NEUTRAL` with off: never acting alone is a choice, not a fault.
 */
export const executionModeToAutomaticExecutionChip = (
  action: EffectiveClaimActionConfig
): AutomaticExecutionChipProps => {
  if (!action.enabled) {
    return { label: copies.actions.automaticExecutionDisabled, tone: AGENT_CHIP_TONE.NEUTRAL };
  }
  if (action.execution_mode === 'always_ai') {
    return { label: executionModeToAutomaticExecutionLabel(action), tone: AGENT_CHIP_TONE.ON };
  }
  if (action.execution_mode === 'above_threshold') {
    return { label: executionModeToAutomaticExecutionLabel(action), tone: AGENT_CHIP_TONE.CONDITIONAL };
  }
  return { label: executionModeToAutomaticExecutionLabel(action), tone: AGENT_CHIP_TONE.NEUTRAL };
};

/**
 * The modes as the edit dialog's selector offers them, in the declaration order of `MODE_LABELS`.
 *
 * Derived rather than listed: `MODE_LABELS` is keyed by the contract's own union, so a mode added
 * backend-side is already a compile error there, and deriving the options from it means the
 * selector gains the new mode with it instead of silently omitting one it cannot offer.
 */
export const EXECUTION_MODE_OPTIONS: { value: ClaimActionExecutionMode; label: string }[] = (
  Object.keys(MODE_LABELS) as ClaimActionExecutionMode[]
).map((value) => ({ value, label: MODE_LABELS[value] }));
